import { DateTime } from "luxon";
import { type FC, type ReactNode, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useScheduleCacheEnabled } from "../../hooks/useScheduleWeekRules";
import { getWeekStart } from "../../utils/dateUtils";
import { DEFAULT_WEEK_START_DAY } from "../../utils/models";
import { routeWeekDateOf } from "../../utils/staffingRoutes";
import { useHospitalData } from "../HospitalDataProvider";
import { useWeekIndex } from "../WeekIndexProvider";
import { RouteWeekContext } from "./RouteWeekContext";

/**
 * The week the staffing page's URL names, from the routes' first render. A page applies its URL's date in an effect,
 * after that render's schedule requests have gone out, so a cold load used to ask for today's weeks before the URL's.
 * With no week shown yet it also keeps this one as the shown week, so the screens above the routes read it as they did
 * when the page applied it.
 */
export const RouteWeekProvider: FC<{ children: ReactNode }> = ({ children }) => {
	const { pathname } = useLocation();
	const { timezone } = useHospitalData();
	const { setShownWeekStart } = useWeekIndex();
	const isCacheEnabled = useScheduleCacheEnabled();
	const routeWeekDate = routeWeekDateOf(pathname);

	const routeWeekStart = useMemo(() => {
		if (routeWeekDate === undefined) return undefined;
		// Parsed in the department's zone, as the pages parse it.
		const date = DateTime.fromISO(routeWeekDate, { zone: timezone });
		return date.isValid ? getWeekStart(date, DEFAULT_WEEK_START_DAY) : undefined;
	}, [routeWeekDate, timezone]);

	useEffect(() => {
		// A week already shown stays: a page that named one in this same commit was applied first, and after the arrows
		// page with `replaceState` the URL still holds the week they left.
		if (isCacheEnabled && routeWeekStart !== undefined) setShownWeekStart((shownWeekStart) => shownWeekStart ?? routeWeekStart);
	}, [isCacheEnabled, routeWeekStart, setShownWeekStart]);

	return <RouteWeekContext.Provider value={routeWeekStart}>{children}</RouteWeekContext.Provider>;
};
